/*
 * <license header>
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Provider, defaultTheme } from '@adobe/react-spectrum';
import { ImportAssetsDialogOpener } from '@zoltar/demo-pilot-sdk';

import useUEContext from '../hooks/useUEContext';
import { uploadAssetsInBatches } from '../lib/uploadAssets';
import { replaceImage, findImagePropertyName } from '../lib/replaceImage';
import { replaceText, extractTextFromDetails, findTextPropertyName } from '../lib/replaceText';
import { runScrape, groupTextsByComponent, mergeTextsByComponent } from '../lib/scrapeSSE';
import { pagePathFromLocation, stripResourceUrn } from '../lib/paths';
import actionWebInvoke from '../utils';
import config from '../config.json';
import ImagesTab from './tabs/ImagesTab';
import TextsTab from './tabs/TextsTab';
import AIGenerateTab from './tabs/AIGenerateTab';
// --- DEV/TEST --- Comment the next 2 lines to remove dev tools before commit
import DevTab from './tabs/DevTab';
import useDevHandlers from '../hooks/useDevHandlers';
// --- /DEV/TEST ---
import './DemoPilotRail.css';

const TABS = [
  { id: 'images', label: 'Images' },
  { id: 'texts', label: 'Texts' },
  { id: 'generate', label: 'Rebrander' },
];

// AIO CLI populates config.json at build time with a map from action id to
// its deployed URL. In `aio app run` the key is `<package>/<action>`.
const UPLOAD_ACTION_URL =
  config['universal-editor-ui-1/upload-to-dam'] ||
  config['upload-to-dam'] ||
  '';
const READ_TEXT_STORAGE_ACTION_URL =
  config['universal-editor-ui-1/read-text-storage'] ||
  config['read-text-storage'] ||
  '';
const WRITE_TEXT_STORAGE_ACTION_URL =
  config['universal-editor-ui-1/write-text-storage'] ||
  config['write-text-storage'] ||
  '';
const REPLACE_ALL_TEXT_ACTION_URL =
  config['universal-editor-ui-1/replace-all-text'] ||
  config['replace-all-text'] ||
  '';
const TEST_LLM_ACTION_URL =
  config['universal-editor-ui-1/test-llm'] ||
  config['test-llm'] ||
  '';
const REPLACE_ALL_PAGE_ACTION_URL =
  config['universal-editor-ui-1/replace-all-page'] ||
  config['replace-all-page'] ||
  '';
const FETCH_NODE_JSON_ACTION_URL =
  config['universal-editor-ui-1/fetch-node-json'] ||
  config['fetch-node-json'] ||
  '';
const REPLACE_PAGE_JCR_ACTION_URL =
  config['universal-editor-ui-1/replace-page-jcr'] ||
  config['replace-page-jcr'] ||
  '';
const ROLLBACK_PAGE_JCR_ACTION_URL =
  config['universal-editor-ui-1/rollback-page-jcr'] ||
  config['rollback-page-jcr'] ||
  '';
const WAL_JCR_ACTION_URL =
  config['universal-editor-ui-1/wal-jcr'] ||
  config['wal-jcr'] ||
  '';

// Safety cap for whole-site operations: never discover/replace more than this
// many pages at once, so we can't accidentally rewrite a huge tree or another
// site. Mirrors DEFAULT_MAX_SITE_PAGES in actions/rebrand/lib.js.
const MAX_SITE_PAGES = 15;

// Keep only pages that live strictly under the resolved site root — never a
// sibling or another site.
function confineToSiteRoot(pages, pagesRoot) {
  const root = (pagesRoot || '').replace(/\/+$/, '');
  if (!root) return pages;
  return (pages || []).filter((p) => p.path === root || p.path.startsWith(`${root}/`));
}

// Convert a replace change plan into WAL entries (before/after per property).
function changesToWalEntries(changes) {
  return (changes || [])
    .filter((c) => c && c.id && c.propertyName)
    .map((c) => ({
      id: c.id,
      propertyName: c.propertyName,
      type: c.type || 'text',
      before: typeof c.currentValue === 'string' ? c.currentValue : '',
      after: typeof c.newValue === 'string' ? c.newValue : '',
    }));
}

// editorActions.update resolves on AEM ack — the canvas iframe needs another
// postMessage round-trip to swap the rendered HTML. We listen for any host
// postMessage that looks like a content-patch / content-update event, with a
// timeout fallback so the success toast always eventually fires. Resolves
// early when the event arrives, capped at `timeoutMs`.
function waitForCanvasUpdate(timeoutMs = 1500) {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener('message', handler);
      clearTimeout(timer);
      resolve();
    };
    const handler = (e) => {
      const data = e && e.data;
      if (!data) return;
      let probe = '';
      try {
        probe = (typeof data === 'string' ? data : JSON.stringify(data)).toLowerCase();
      } catch (_) {
        return;
      }
      // Catch any of the variants UE emits — without a documented event name
      // we match a broad pattern. Worst case: we miss the event and fall back
      // to the timeout.
      if (
        probe.indexOf('content-patch') !== -1 ||
        probe.indexOf('content_patched') !== -1 ||
        probe.indexOf('contentpatched') !== -1 ||
        probe.indexOf('aue:content') !== -1 ||
        probe.indexOf('component-updated') !== -1
      ) {
        finish();
      }
    };
    window.addEventListener('message', handler);
    const timer = setTimeout(finish, timeoutMs);
  });
}

function resolveComponentInfo(editableId, editables) {
  const byId = {};
  for (const e of editables) { if (e && e.id) byId[e.id] = e; }
  const self = byId[editableId];
  if (!self) return { component: '', parent: '' };
  const component = self.label || '';
  const parentNode = self.parentid ? byId[self.parentid] : null;
  const parent = parentNode?.label || '';
  return { component, parent };
}

const ACCEPTED_TEXT_PROPS = new Set(['text', 'title', 'jcr:title']);

function resolvePropertyValue(details, propName) {
  const containers = [details.data, details.properties, details.model, details]
    .filter((x) => x && typeof x === 'object');
  for (const obj of containers) {
    if (typeof obj[propName] === 'string') return obj[propName];
  }
  return '';
}

function collectTextEntry(details, ed, index, editables) {
  const propName = findTextPropertyName(details);
  if (!propName || !ACCEPTED_TEXT_PROPS.has(propName)) return null;
  const currentText = resolvePropertyValue(details, propName);
  if (!currentText || currentText.length < 2) return null;
  const id = ed.id || `editable-${index}`;
  const { component, parent } = resolveComponentInfo(ed.id, editables);
  const hasHtml = /<[a-z][\s\S]*>/i.test(currentText);
  return { id, propertyName: propName, currentText, component, parent, hasHtml };
}

async function getEditorContext(ue) {
  const editorActions = ue.conn?.host?.editorActions;
  if (!editorActions || typeof editorActions.details !== 'function') return null;
  const editorState = await ue.conn.host.editorState.get();
  return { editorActions, editorState, editables: editorState?.editables || [] };
}

const DAM_PAGE_SIZE = 100;
const DAM_MAX_ASSETS = 500;

async function fetchDamEntities(ue) {
  const slug = (ue.damFolderPath || '').replace(/^\/content\/dam\//, '');
  const allEntities = [];
  let offset = 0;

  while (offset < DAM_MAX_ASSETS) {
    const resp = await actionWebInvoke(
      FETCH_NODE_JSON_ACTION_URL,
      { Authorization: `Bearer ${ue.imsToken}` },
      { authorUrl: ue.authorUrl, orgId: ue.orgId, bearer: ue.imsToken, resource: `/api/assets/${slug}`, queryString: `limit=${DAM_PAGE_SIZE}&offset=${offset}` },
      { method: 'POST' }
    );
    const entities = (resp?.entities || []).filter((e) => e && e.properties);
    if (entities.length === 0) break;
    allEntities.push(...entities);
    const apiTotal = resp?.properties?.['srn:paging']?.total ?? 0;
    if (!apiTotal || offset + entities.length >= apiTotal || entities.length < DAM_PAGE_SIZE) break;
    offset += entities.length;
  }

  const folders = allEntities.filter((e) => (e.class || []).includes('assets/folder'));
  const assets = allEntities.filter((e) => !(e.class || []).includes('assets/folder'));
  return { allEntities, folders, assets, total: assets.length };
}

async function applyReplacements(editorActions, editableMap, replacements, onProgress, delayMs = 0) {
  let applied = 0;
  let failed = 0;
  const delay = delayMs > 0 ? (ms) => new Promise((r) => setTimeout(r, ms)) : null;

  for (const rep of replacements) {
    const entry = editableMap[rep.id];
    if (!entry) { failed++; continue; }

    const patches = [];
    if (rep.newText && entry.textPropertyName) {
      patches.push({ op: 'replace', path: `/${entry.textPropertyName}`, value: rep.newText });
    }
    if (rep.newImage && entry.imagePropertyName) {
      patches.push({ op: 'replace', path: `/${entry.imagePropertyName}`, value: rep.newImage });
    }
    if (patches.length === 0) { failed++; continue; }

    try {
      await editorActions.update({ target: { editable: entry.editable }, patch: patches });
      applied++;
    } catch (err) {
      console.warn('[DemoPilot] update failed for', rep.id, err);
      failed++;
    }
    if (delay) await delay(delayMs);
    if (onProgress) onProgress(applied + failed, replacements.length);
  }
  return { applied, failed };
}

export default function DemoPilotRail() {
  const ue = useUEContext();
  const [activeTab, setActiveTab] = useState('images');
  // Assets freshly scraped from Zoltar, waiting to be uploaded.
  // Shape: { images: Array<{src, alt, ...}>, texts: [...] }
  const [pendingAssets, setPendingAssets] = useState(null);
  // Scraped texts, keyed by component (banner/button/hero/...). Persisted to
  // AEM at /var/text-storage/{siteSlug} so it survives reloads.
  const [texts, setTexts] = useState({});
  // Mirror of `texts` we can read synchronously from inside the scrape effect.
  const textsRef = useRef({});
  // Aggregated upload status: { inProgress: bool, total, done, failed }.
  const [uploadStatus, setUploadStatus] = useState({ inProgress: false, total: 0, done: 0, failed: 0 });
  // Mount-time DAM folder listing state. Defaults to true so the grid shows
  // skeletons during the brief window before UE attaches and the list runs.
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  // Phase 4: Replace flow state.
  //   replacingId: the image card currently mid-replace (or '').
  //   replacingTextId: the text chip currently mid-replace (or '').
  const [replacingId, setReplacingId] = useState('');
  const [replacingTextId, setReplacingTextId] = useState('');
  // Current text on the selected canvas node (best-effort). Used by TextsTab
  // to surface "Best fits" — chips that look similar (length, capitalization,
  // word count) get sorted to the top so the user doesn't have to scan a long
  // list. Empty when no selection, no text-bearing property, or fetch failed.
  const [selectedText, setSelectedText] = useState('');
  // Bumped after each upload batch completes — ImagesTab uses this to remount
  // the asset selector so freshly-uploaded assets appear in the list.
  const [selectorRefreshKey, setSelectorRefreshKey] = useState(0);
  // Replace All Text flow state.
  const [replaceAllStatus, setReplaceAllStatus] = useState({
    phase: 'idle', // idle | collecting | generating | applying | done | error
    progress: { done: 0, total: 0 },
    error: '',
  });

  // Replace All Page flow state (texts + images).
  const [replacePageStatus, setReplacePageStatus] = useState({
    phase: 'idle',
    progress: { done: 0, total: 0 },
    error: '',
  });
  // Post-replace refresh dialog — shown after Replace All completes so the
  // user knows to CMD+R (iframe can't be reloaded programmatically).
  const [showRefreshDialog, setShowRefreshDialog] = useState(false);
  // AI-Generate tab: user-provided context injected into the LLM prompt.
  const [targetBrand, setTargetBrand] = useState('');
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [pageType, setPageType] = useState('');
  const [language, setLanguage] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  // DAM asset count — fetched once when UE context is ready.
  const [damAssetCount, setDamAssetCount] = useState(null); // null = not fetched, number = count

  // Replace All Pages flow state (iterates over every page on the site).
  const [replaceAllPagesStatus, setReplaceAllPagesStatus] = useState({
    phase: 'idle', // idle | listing | selecting | replacing | done | error
    currentPage: '',
    progress: { done: 0, total: 0 },
    results: [],
    error: '',
  });

  // Discovered pages (from "Discover" step) and user selection.
  const [discoveredPages, setDiscoveredPages] = useState([]); // [{ name, path, title, depth }]
  const [selectedPagePaths, setSelectedPagePaths] = useState(new Set());

  // Change history (WAL) — named, multi-step timeline with revert/redo.
  const [walHistory, setWalHistory] = useState({ head: 0, maxSeq: 0, steps: [], loaded: false });
  const [walBusy, setWalBusy] = useState(false); // true while listing or restoring
  const [revertConfirm, setRevertConfirm] = useState(null); // { seq, name, direction } | null

  const uploadingRef = useRef(false);
  // One-shot guard for the initial text-storage read.
  const didReadTextsRef = useRef(false);
  const didFetchDamCountRef = useRef(false);

  // One-shot debug log: confirm token/author/slug/selection populate when
  // opened inside UE.
  useEffect(() => {
    if (!ue.ready) return;
    // eslint-disable-next-line no-console
    console.log('[DemoPilot] UE context ready:', {
      hasToken: !!ue.imsToken,
      orgId: ue.orgId,
      authorUrl: ue.authorUrl,
      repositoryId: ue.repositoryId,
      sitePath: ue.sitePath,
      siteSlug: ue.siteSlug,
      damFolderPath: ue.damFolderPath,
      selection: ue.selection,
      uploadActionUrl: UPLOAD_ACTION_URL || '(not configured)',
    });
  }, [ue.ready]);

  // Mount-time: hydrate the texts state from /var/text-storage/{siteSlug} so
  // previously-scraped texts survive a rail remount / page reload.
  useEffect(() => {
    if (didReadTextsRef.current) return;
    if (!ue.ready) return;
    if (!ue.imsToken || !ue.authorUrl || !ue.siteSlug) return;
    if (!READ_TEXT_STORAGE_ACTION_URL) {
      console.warn('[DemoPilot] READ_TEXT_STORAGE_ACTION_URL is empty — cannot load saved texts');
      return;
    }
    didReadTextsRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const resp = await actionWebInvoke(
          READ_TEXT_STORAGE_ACTION_URL,
          { Authorization: `Bearer ${ue.imsToken}` },
          {
            siteSlug: ue.siteSlug,
            authorUrl: ue.authorUrl,
            orgId: ue.orgId,
            bearer: ue.imsToken,
          },
          { method: 'POST' }
        );
        if (cancelled) return;
        const stored = (resp && resp.texts) || (resp && resp.body && resp.body.texts) || {};
        if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) {
          console.log('[DemoPilot] hydrated texts from storage:', Object.keys(stored));
          textsRef.current = stored;
          setTexts(stored);
        } else {
          console.log('[DemoPilot] no stored texts for site', ue.siteSlug);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[DemoPilot] read-text-storage failed:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [ue.ready, ue.imsToken, ue.authorUrl, ue.siteSlug, ue.orgId]);

  // Fetch DAM asset count once when the UE context resolves the DAM folder.
  useEffect(() => {
    if (didFetchDamCountRef.current) return;
    if (!ue.ready || !ue.imsToken || !ue.authorUrl || !ue.damFolderPath) return;
    if (!FETCH_NODE_JSON_ACTION_URL) return;
    didFetchDamCountRef.current = true;

    (async () => {
      try {
        const { assets } = await fetchDamEntities(ue);
        setDamAssetCount(assets.length);
        console.log(`[DemoPilot] DAM asset count: ${assets.length}`);
      } catch (err) {
        console.warn('[DemoPilot] DAM count fetch failed:', err?.message || err);
        setDamAssetCount(0);
      }
    })();
  }, [ue.ready, ue.imsToken, ue.authorUrl, ue.orgId, ue.damFolderPath]);

  // When the canvas selection changes, fetch the current text on that node
  // so TextsTab can rank chips against it. Best-effort: silent on failure
  // (the panel still works — Best fits just stays empty).
  useEffect(() => {
    const editable = ue.selection?.editable;
    const detailsFn = ue.conn?.host?.editorActions?.details;
    if (!editable || typeof detailsFn !== 'function') {
      setSelectedText('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const details = await detailsFn(editable);
        if (cancelled) return;
        const { value } = extractTextFromDetails(details);
        setSelectedText(value || '');
      } catch (err) {
        if (cancelled) return;
        console.warn('[DemoPilot] selected-text fetch failed:', err && (err.message || err));
        setSelectedText('');
      }
    })();
    return () => { cancelled = true; };
  }, [ue.selection?.selectedId, ue.conn]);

  // Phase 2: when new pendingAssets arrive, prepend them to the grid and
  // kick off the batch upload pipeline.
  // When new pendingAssets arrive, kick off the upload pipeline and persist
  // any scraped texts.
  useEffect(() => {
    if (!pendingAssets) return;

    // Merge scraped texts in regardless of the image upload path.
    const grouped = groupTextsByComponent(pendingAssets.texts);
    if (Object.keys(grouped).length > 0) {
      const merged = mergeTextsByComponent(textsRef.current, grouped);
      textsRef.current = merged;
      setTexts(merged);
      if (WRITE_TEXT_STORAGE_ACTION_URL && ue.siteSlug && ue.imsToken && ue.authorUrl) {
        actionWebInvoke(
          WRITE_TEXT_STORAGE_ACTION_URL,
          { Authorization: `Bearer ${ue.imsToken}` },
          {
            siteSlug: ue.siteSlug,
            authorUrl: ue.authorUrl,
            orgId: ue.orgId,
            bearer: ue.imsToken,
            texts: merged,
          },
          { method: 'POST' }
        ).then(
          (resp) => console.log('[DemoPilot] text-storage write ok:', resp && resp.path),
          (err) => console.error('[DemoPilot] text-storage write failed:', err)
        );
      }
    }

    const incomingImages = (pendingAssets.images || []).filter((i) => i && typeof i.src === 'string' && i.src);
    if (incomingImages.length === 0) {
      setPendingAssets(null);
      return;
    }

    if (uploadingRef.current) {
      console.warn('[DemoPilot] upload already in progress, dropping new import');
      setPendingAssets(null);
      return;
    }
    if (!ue.imsToken || !ue.authorUrl || !ue.damFolderPath) {
      console.warn('[DemoPilot] UE context incomplete — cannot upload', {
        hasToken: !!ue.imsToken, authorUrl: ue.authorUrl, damFolderPath: ue.damFolderPath,
      });
      setPendingAssets(null);
      return;
    }
    if (!UPLOAD_ACTION_URL) {
      console.warn('[DemoPilot] UPLOAD_ACTION_URL is empty — build-time config.json missing');
      setPendingAssets(null);
      return;
    }

    setUploadStatus({ inProgress: true, total: incomingImages.length, done: 0, failed: 0 });
    uploadingRef.current = true;

    (async () => {
      try {
        const urls = incomingImages.map((i) => i.isLogo ? { url: i.src, name: 'LOGO' } : i.src);
        const ctx = {
          actionUrl: UPLOAD_ACTION_URL,
          imsToken: ue.imsToken,
          authorUrl: ue.authorUrl,
          orgId: ue.orgId,
          targetFolderPath: ue.damFolderPath,
        };
        for await (const result of uploadAssetsInBatches(urls, ctx)) {
          setUploadStatus((prev) => ({
            ...prev,
            done: prev.done + (result.ok ? 1 : 0),
            failed: prev.failed + (result.ok ? 0 : 1),
          }));
        }
      } catch (err) {
        console.error('[DemoPilot] upload pipeline failed:', err);
      } finally {
        uploadingRef.current = false;
        setUploadStatus((prev) => ({ ...prev, inProgress: false }));
        setPendingAssets(null);
        // Bump so the AEM Assets Selector remounts and picks up new assets.
        setSelectorRefreshKey((k) => k + 1);
      }
    })();
  }, [pendingAssets, ue.imsToken, ue.authorUrl, ue.damFolderPath, ue.orgId, ue.siteSlug]);

  // Show a UE-native toast via editorActions.toast(variant, message, timeout).
  // No-ops silently if the host doesn't expose toast (older SDK versions).
  const showToast = (variant, message, timeout = 4000) => {
    const fn = ue?.conn?.host?.editorActions?.toast;
    if (typeof fn === 'function') {
      try { fn(variant, message, timeout); } catch (e) { console.warn('[DemoPilot] toast failed:', e); }
    } else {
      console.log(`[DemoPilot] toast (${variant}): ${message}`);
    }
  };

  // Called by ImagesTab when the user clicks an asset in the AEM Assets
  // Selector. Replaces the image on the currently-selected canvas element.
  const handleAssetPick = async (damPath, asset) => {
    if (!damPath) return;
    if (!ue.selection?.cleanResource) {
      showToast('negative', 'Select an image element in the canvas first');
      return;
    }
    const result = await replaceImage({
      damPath,
      ue,
      onStatus: (msg) => console.log('[DemoPilot] replace status:', msg),
    });
    if (result.ok) {
      // editorActions.update resolves once AEM acks the patch, but the canvas
      // iframe needs another postMessage round-trip to fetch and swap the
      // component HTML. Wait until that lands (or a timeout) so the toast
      // lines up with the visible change.
      await waitForCanvasUpdate(1500);
      const name = (asset && (asset['repo:name'] || asset['repo:path'])) || damPath;
      showToast('positive', `Replaced with ${name}`);
    } else {
      showToast('negative', result.error || 'replace failed');
    }
  };

  // Replace the text on the currently-selected UE node with the text from a
  // scraped chip. Returns true on success so TextsTab can flash a "replaced"
  // confirmation on the clicked chip.
  const handleTextReplace = async (id, text) => {
    if (typeof text !== 'string') return false;
    if (!ue.selection?.cleanResource) {
      showToast('negative', 'Select a text node in the canvas first');
      return false;
    }
    if (replacingTextId) return false;

    setReplacingTextId(id);

    const result = await replaceText({
      text,
      ue,
      onStatus: (msg) => console.log('[DemoPilot] text replace status:', msg),
    });

    setReplacingTextId('');
    if (result.ok) {
      showToast('positive', `Replaced (${result.propertyName})`);
      return true;
    }
    showToast('negative', result.error || 'text replace failed');
    return false;
  };

  // Fetch brand context (toneOfVoice, brandGuidelines, pageDescription) from the
  // reference URL when the user has provided one. Best-effort — never blocks the
  // rebrand flow; returns null on failure.
  const fetchBrandContext = useCallback(async (url) => {
    if (!url || !url.startsWith('https://')) return null;
    if (!ue.imsToken) return null;
    try {
      const result = await runScrape({
        url,
        params: { toneOfVoice: true, brandGuidelines: true, pageDescription: true, images: false, texts: false },
        token: ue.imsToken,
      });
      const { toneOfVoice, brandGuidelines, pageDescription } = result;
      if (!toneOfVoice && !brandGuidelines && !pageDescription) return null;
      return { toneOfVoice, brandGuidelines, pageDescription };
    } catch (e) {
      console.warn('[DemoPilot] brand context scrape failed:', e?.message || e);
      return null;
    }
  }, [ue.imsToken]);

  const handleReplaceAllText = useCallback(async () => {
    if (!REPLACE_ALL_TEXT_ACTION_URL) { showToast('negative', 'Replace All Text action not deployed yet'); return; }
    const setStatus = (s) => setReplaceAllStatus(typeof s === 'function' ? s : () => s);
    const err = (msg) => setStatus({ phase: 'error', progress: { done: 0, total: 0 }, error: msg });

    let ctx;
    try { ctx = await getEditorContext(ue); } catch (_) { /* fall through */ }
    if (!ctx) { showToast('negative', 'editorActions not available'); return; }
    const { editorActions, editables } = ctx;

    setStatus({ phase: 'collecting', progress: { done: 0, total: 0 }, error: '' });
    const pageTexts = [];
    const editableMap = {};
    for (let i = 0; i < editables.length; i++) {
      const ed = editables[i];
      if (!ed || !ed.resource) continue;
      try {
        const details = await editorActions.details(ed);
        const entry = collectTextEntry(details, ed, i, editables);
        if (!entry) continue;
        pageTexts.push(entry);
        editableMap[entry.id] = { editable: ed, textPropertyName: entry.propertyName };
      } catch (_) { /* skip */ }
      setStatus((s) => ({ ...s, progress: { done: i + 1, total: editables.length } }));
    }
    if (pageTexts.length === 0) { err('No text nodes found on the page'); return; }

    setStatus({ phase: 'generating', progress: { done: 0, total: pageTexts.length }, error: '' });
    const brandContext = await fetchBrandContext(scrapeUrl);
    let replacements;
    try {
      const resp = await actionWebInvoke(
        REPLACE_ALL_TEXT_ACTION_URL,
        { Authorization: `Bearer ${ue.imsToken}` },
        { pageTexts, scrapedTexts: texts, brandContext: brandContext || undefined },
        { method: 'POST' }
      );
      replacements = resp?.replacements || resp?.body?.replacements || [];
    } catch (e) {
      console.error('[DemoPilot] replace-all-text failed:', e);
      err(e?.message || 'Gemini API call failed'); return;
    }
    if (!Array.isArray(replacements) || replacements.length === 0) { err('No replacements returned by AI'); return; }

    setStatus({ phase: 'applying', progress: { done: 0, total: replacements.length }, error: '' });
    const { applied, failed } = await applyReplacements(
      editorActions, editableMap, replacements,
      (done, total) => setStatus((s) => ({ ...s, progress: { done, total } }))
    );

    await waitForCanvasUpdate(2000);
    showToast('positive', `Replaced ${applied} text${applied !== 1 ? 's' : ''}${failed > 0 ? ` (${failed} failed)` : ''}`);
    setStatus({ phase: 'done', progress: { done: applied, total: replacements.length }, error: '' });
    setShowRefreshDialog(true);
    setTimeout(() => setStatus({ phase: 'idle', progress: { done: 0, total: 0 }, error: '' }), 4000);
  }, [ue.conn, ue.imsToken, texts, scrapeUrl, fetchBrandContext]);


  // Collect all page components with their text AND image properties,
  // grouped by parent component. Each entry represents a content unit
  // the LLM can replace (text, image, or both).
  const collectPageComponents = useCallback(async (editorActions, editorState, onProgress) => {
    const editables = editorState?.editables || [];
    const components = [];
    const editableMap = {};

    for (let i = 0; i < editables.length; i++) {
      const ed = editables[i];
      if (!ed || !ed.resource) continue;
      try {
        const details = await editorActions.details(ed);

        const textProp = findTextPropertyName(details);
        const imgProp = findImagePropertyName(details);

        let currentText = '';
        let hasHtml = false;
        const resolvedTextProp = (textProp && ACCEPTED_TEXT_PROPS.has(textProp)) ? textProp : undefined;
        if (resolvedTextProp) {
          currentText = resolvePropertyValue(details, resolvedTextProp);
          hasHtml = /<[a-z][\s\S]*>/i.test(currentText);
        }

        const currentImage = imgProp ? resolvePropertyValue(details, imgProp) : '';

        if ((!currentText || currentText.length < 2) && !currentImage) continue;

        const id = ed.id || `editable-${i}`;
        const { component, parent } = resolveComponentInfo(ed.id, editables);
        // Clean JCR node path (URN stripped) — needed so a server-side snapshot
        // can be rolled back via Sling POST, since ed.id is a UE URN, not a path.
        const jcrPath = stripResourceUrn(ed.resource);

        components.push({
          id, jcrPath, component, parent,
          currentText: currentText || undefined,
          textPropertyName: resolvedTextProp,
          hasHtml: hasHtml || undefined,
          currentImage: currentImage || undefined,
          imagePropertyName: imgProp || undefined,
        });
        editableMap[id] = { editable: ed, textPropertyName: resolvedTextProp, imagePropertyName: imgProp || undefined };
      } catch (_) {
        // skip
      }
      if (typeof onProgress === 'function') onProgress(i + 1, editables.length);
    }
    return { components, editableMap };
  }, []);

  // Append a named step to the change-history WAL. Best-effort — never blocks
  // the replace flow, since the write to AEM already succeeded.
  const appendWalStep = useCallback(async (name, pages, entries) => {
    if (!WAL_JCR_ACTION_URL) { console.warn('[DemoPilot] WAL skip: WAL_JCR_ACTION_URL empty (redeploy needed)'); return null; }
    if (!ue.siteSlug) { console.warn('[DemoPilot] WAL skip: no siteSlug'); return null; }
    if (!entries || entries.length === 0) { console.warn('[DemoPilot] WAL skip: no entries to log'); return null; }
    try {
      console.log(`[DemoPilot] WAL append "${name}" — ${entries.length} entries, ${(pages || []).length} pages`);
      const resp = await actionWebInvoke(
        WAL_JCR_ACTION_URL,
        { Authorization: `Bearer ${ue.imsToken}` },
        { mode: 'append', authorUrl: ue.authorUrl, siteSlug: ue.siteSlug, orgId: ue.orgId, bearer: ue.imsToken, name: name || 'rebrand', pages: pages || [], entries },
        { method: 'POST' }
      );
      const body = resp?.body || resp || null;
      if (body && body.error) console.warn('[DemoPilot] WAL append error:', body.error);
      else console.log('[DemoPilot] WAL step appended:', body && body.seq, body && body.name);
      return body;
    } catch (err) {
      console.warn('[DemoPilot] WAL append failed:', err?.message || err);
      return null;
    }
  }, [ue.imsToken, ue.authorUrl, ue.orgId, ue.siteSlug]);

  // Load the change-history timeline from the WAL.
  const refreshWalHistory = useCallback(async () => {
    if (!WAL_JCR_ACTION_URL || !ue.siteSlug || !ue.imsToken || !ue.authorUrl) return;
    setWalBusy(true);
    try {
      const resp = await actionWebInvoke(
        WAL_JCR_ACTION_URL,
        { Authorization: `Bearer ${ue.imsToken}` },
        { mode: 'list', authorUrl: ue.authorUrl, siteSlug: ue.siteSlug, orgId: ue.orgId, bearer: ue.imsToken },
        { method: 'POST' }
      );
      const body = resp?.body || resp || {};
      setWalHistory({ head: body.head || 0, maxSeq: body.maxSeq || 0, steps: body.steps || [], loaded: true });
    } catch (err) {
      console.warn('[DemoPilot] WAL history load failed:', err?.message || err);
      setWalHistory((h) => ({ ...h, loaded: true }));
    } finally {
      setWalBusy(false);
    }
  }, [ue.imsToken, ue.authorUrl, ue.orgId, ue.siteSlug]);

  const handleReplaceAllPage = useCallback(async () => {
    if (!REPLACE_ALL_PAGE_ACTION_URL) { showToast('negative', 'Replace All Page action not deployed yet'); return; }
    const setStatus = (s) => setReplacePageStatus(typeof s === 'function' ? s : () => s);
    const err = (msg) => setStatus({ phase: 'error', progress: { done: 0, total: 0 }, error: msg });

    let ctx;
    try { ctx = await getEditorContext(ue); } catch (_) { /* fall through */ }
    if (!ctx) { showToast('negative', 'editorActions not available'); return; }

    setStatus({ phase: 'collecting', progress: { done: 0, total: 0 }, error: '' });
    const { components, editableMap } = await collectPageComponents(
      ctx.editorActions, ctx.editorState,
      (done, total) => setStatus((s) => ({ ...s, progress: { done, total } }))
    );
    if (components.length === 0) { err('No components found on the page'); return; }

    // Step 1: detect logo in DAM — only apply on nav/header pages or when image path contains "logo"
    const LOGO_PATH_RE = /logo/i;
    const pageUrl = ctx.editorState?.location || '';
    const isNavPage = /\/(nav|header)(\.html)?$/i.test(pageUrl);
    // Derive the page path from the live editor location (more reliable than the
    // polled ue.pagePath), so the snapshot is keyed correctly for undo.
    const pagePath = pagePathFromLocation(pageUrl) || ue.pagePath || '';

    let logoApplied = false;
    let logoTargetComp = null;
    try {
      const { assets } = await fetchDamEntities(ue);
      const logoEntity = assets.find((e) => {
        const name = e.properties?.name || e.properties?.['repo:name'] || '';
        return /^LOGO(\.|$)/i.test(name);
      });
      if (logoEntity) {
        const logoPath = logoEntity.href?.replace(/\/api\/assets/, '/content/dam').replace(/\.json$/, '') || '';
        if (isNavPage) {
          const first = components.find((c) => c.imagePropertyName);
          if (first) logoTargetComp = { comp: first, path: logoPath, reason: 'nav-page' };
        } else {
          for (const c of components) {
            if (!c.imagePropertyName) continue;
            if (LOGO_PATH_RE.test(c.currentImage || '')) {
              logoTargetComp = { comp: c, path: logoPath, reason: 'image-path' }; break;
            }
          }
        }
        if (logoTargetComp) {
          console.log(`[DemoPilot] logo detected (${logoTargetComp.reason}) → applying to "${logoTargetComp.comp.id}" before LLM`);
          setStatus({ phase: 'applying', progress: { done: 0, total: 1 }, error: '' });
          try {
            const entry = editableMap[logoTargetComp.comp.id];
            await ctx.editorActions.update({
              target: { editable: entry.editable },
              patch: [{ op: 'replace', path: `/${entry.imagePropertyName}`, value: logoTargetComp.path }],
            });
            logoApplied = true;
            showToast('positive', 'Logo applied');
          } catch (logoErr) {
            console.warn('[DemoPilot] logo apply failed:', logoErr);
          }
        } else {
          console.log('[DemoPilot] logo in DAM but no header/nav component found — skipping logo');
        }
      }
    } catch (damErr) {
      console.warn('[DemoPilot] logo DAM check failed:', damErr);
    }

    // Step 2: call LLM for replacements
    setStatus({ phase: 'generating', progress: { done: 0, total: components.length }, error: '' });
    const brandContext = await fetchBrandContext(scrapeUrl);
    let replacements;
    let snapshotSaved = false;
    try {
      const actionBody = {
        pageComponents: components, scrapedTexts: texts,
        damFolderPath: ue.damFolderPath || '', authorUrl: ue.authorUrl,
        orgId: ue.orgId, bearer: ue.imsToken,
        pageUrl,
        siteSlug: ue.siteSlug || undefined,
        pagePath: pagePath || undefined,
        targetBrand: targetBrand || undefined,
        pageType: pageType || undefined,
        language: language || undefined,
        additionalContext: additionalContext || undefined,
        brandContext: brandContext || undefined,
      };
      console.log(`[DemoPilot] replace-all-page — ${components.length} components, logo=${logoApplied}, isNavPage=${isNavPage}, ${(JSON.stringify(actionBody).length / 1024).toFixed(1)}KB`);
      const resp = await actionWebInvoke(
        REPLACE_ALL_PAGE_ACTION_URL,
        { Authorization: `Bearer ${ue.imsToken}` },
        actionBody, { method: 'POST' }
      );
      replacements = resp?.replacements || resp?.body?.replacements || [];
      snapshotSaved = resp?.snapshotSaved ?? resp?.body?.snapshotSaved ?? false;
    } catch (e) {
      console.error('[DemoPilot] replace-all-page failed:', e);
      err(e?.message || 'AI call failed'); return;
    }
    if (!Array.isArray(replacements) || replacements.length === 0) { err('No replacements returned by AI'); return; }

    // Step 3: apply LLM text replacements
    setStatus({ phase: 'applying', progress: { done: 0, total: replacements.length }, error: '' });
    const { applied, failed } = await applyReplacements(
      ctx.editorActions, editableMap, replacements,
      (done, total) => setStatus((s) => ({ ...s, progress: { done, total } })),
      300
    );

    const totalApplied = applied + (logoApplied ? 1 : 0);
    showToast('positive', `Replaced ${totalApplied} component${totalApplied !== 1 ? 's' : ''}${logoApplied ? ' (incl. logo)' : ''}${failed > 0 ? ` (${failed} failed)` : ''}`);
    setStatus({ phase: 'done', progress: { done: applied, total: replacements.length }, error: '' });

    // Record this single-page replacement as a named history step (join the
    // collected components' original values with the applied replacements).
    if (pagePath) {
      const compById = Object.fromEntries(components.map((c) => [c.id, c]));
      const walEntries = [];
      for (const rep of replacements) {
        const c = compById[rep.id];
        if (!c || !c.jcrPath) continue;
        if (rep.newText && c.textPropertyName) {
          walEntries.push({ id: c.jcrPath, propertyName: c.textPropertyName, type: 'text', before: c.currentText || '', after: rep.newText });
        }
        if (rep.newImage && c.imagePropertyName) {
          walEntries.push({ id: c.jcrPath, propertyName: c.imagePropertyName, type: 'image', before: c.currentImage || '', after: rep.newImage });
        }
      }
      if (logoApplied && logoTargetComp && logoTargetComp.comp?.jcrPath && logoTargetComp.comp.imagePropertyName) {
        walEntries.push({ id: logoTargetComp.comp.jcrPath, propertyName: logoTargetComp.comp.imagePropertyName, type: 'image', before: logoTargetComp.comp.currentImage || '', after: logoTargetComp.path });
      }
      if (walEntries.length > 0) {
        await appendWalStep((targetBrand || '').trim() || 'Rebrand', [pagePath], walEntries);
        refreshWalHistory();
      }
    }

    setShowRefreshDialog(true);
    setTimeout(() => setStatus({ phase: 'idle', progress: { done: 0, total: 0 }, error: '' }), 4000);
  }, [ue.conn, ue.imsToken, ue.authorUrl, ue.orgId, ue.damFolderPath, ue.siteSlug, ue.pagePath, texts, targetBrand, pageType, language, additionalContext, scrapeUrl, fetchBrandContext, collectPageComponents, appendWalStep, refreshWalHistory]);

  // Shared recursive walker for Sling JSON tree → flat page list.
  const SKIP_PAGES = useMemo(() => new Set(['placeholders', 'redirects', 'article-template']), []);
  const collectPagesFromTree = useCallback((node, parentPath, rootDepth) => {
    const found = [];
    for (const [key, val] of Object.entries(node)) {
      if (!val || typeof val !== 'object' || val['jcr:primaryType'] !== 'cq:Page') continue;
      if (SKIP_PAGES.has(key)) continue;
      const jcrContent = val['jcr:content'] || {};
      const pagePath = `${parentPath}/${key}`;
      const depth = pagePath.split('/').length - rootDepth;
      const isNav = key === 'nav' || key === 'header';
      const isFooter = key === 'footer';
      found.push({
        name: key,
        path: pagePath,
        title: jcrContent['jcr:title'] || key,
        depth,
        isNav,
        isFooter,
        hidden: jcrContent.hideInNav === true || jcrContent.hideInNav === 'true',
      });
      found.push(...collectPagesFromTree(val, pagePath, rootDepth));
    }
    return found;
  }, [SKIP_PAGES]);

  // Step 1: Discover all pages on the site.
  const handleDiscoverPages = useCallback(async () => {
    if (!FETCH_NODE_JSON_ACTION_URL) { showToast('negative', 'fetch-node-json action not configured'); return; }
    if (!ue.pagesRoot || !ue.imsToken || !ue.authorUrl) {
      showToast('negative', `UE context incomplete (pagesRoot="${ue.pagesRoot || ''}", token=${!!ue.imsToken})`); return;
    }
    const setStatus = (s) => setReplaceAllPagesStatus(typeof s === 'function' ? s : () => s);
    const resource = ue.pagesRoot;
    const rootName = resource.split('/').pop() || 'root';
    const rootDepth = resource.split('/').length;
    console.log(`[DemoPilot] discoverPages — listing children of ${resource}`);
    setStatus({ phase: 'listing', currentPage: '', progress: { done: 0, total: 0 }, results: [], error: '' });

    try {
      const resp = await actionWebInvoke(
        FETCH_NODE_JSON_ACTION_URL,
        { Authorization: `Bearer ${ue.imsToken}` },
        { authorUrl: ue.authorUrl, orgId: ue.orgId, bearer: ue.imsToken, resource: `${resource}.3`, queryString: '' },
        { method: 'POST' }
      );
      const json = resp?.body || resp || {};
      let pages = collectPagesFromTree(json, resource, rootDepth);
      pages.unshift({ name: rootName, path: resource, title: 'Homepage', depth: 0, isNav: false, isFooter: false, hidden: false });

      // Safety — confinement: never keep a page outside the resolved site root.
      const before = pages.length;
      pages = confineToSiteRoot(pages, resource);
      if (pages.length < before) {
        console.warn(`[DemoPilot] dropped ${before - pages.length} page(s) outside ${resource}`);
      }

      if (pages.length === 0) {
        setStatus({ phase: 'error', currentPage: '', progress: { done: 0, total: 0 }, results: [], error: 'No pages found' });
        return;
      }
      setDiscoveredPages(pages);
      // Safety — page cap: pre-select content pages up to MAX_SITE_PAGES only, so
      // a big site never gets bulk-replaced by accident. The user can still tick
      // more manually, but the replace step enforces the same hard cap.
      const selectable = pages.filter((p) => !p.isNav && !p.isFooter);
      setSelectedPagePaths(new Set(selectable.slice(0, MAX_SITE_PAGES).map((p) => p.path)));
      if (selectable.length > MAX_SITE_PAGES) {
        showToast('info', `${selectable.length} pages found — for safety only the first ${MAX_SITE_PAGES} are pre-selected. You can replace at most ${MAX_SITE_PAGES} at a time.`);
      }
      setStatus({ phase: 'selecting', currentPage: '', progress: { done: 0, total: pages.length }, results: [], error: '' });
    } catch (err) {
      setStatus({ phase: 'error', currentPage: '', progress: { done: 0, total: 0 }, results: [], error: `Failed to list pages: ${err?.message || err}` });
    }
  }, [ue.pagesRoot, ue.imsToken, ue.authorUrl, ue.orgId, collectPagesFromTree]);

  // Step 2: Replace only the selected pages.
  const handleReplaceSelectedPages = useCallback(async () => {
    if (!REPLACE_PAGE_JCR_ACTION_URL) { showToast('negative', 'replace-page-jcr action not deployed'); return; }
    let pages = discoveredPages.filter((p) => selectedPagePaths.has(p.path));
    if (pages.length === 0) { showToast('negative', 'No pages selected'); return; }

    // Safety — confinement: never replace a page outside the resolved site root.
    const confined = confineToSiteRoot(pages, ue.pagesRoot);
    if (confined.length < pages.length) {
      showToast('negative', `${pages.length - confined.length} selected page(s) are outside this site and were blocked.`);
      pages = confined;
    }
    // Safety — hard cap: refuse to replace more than MAX_SITE_PAGES at once.
    if (pages.length > MAX_SITE_PAGES) {
      showToast('negative', `Too many pages selected (${pages.length}). For safety you can replace at most ${MAX_SITE_PAGES} at a time — deselect some.`);
      return;
    }

    const setStatus = (s) => setReplaceAllPagesStatus(typeof s === 'function' ? s : () => s);
    const results = [];
    setStatus({ phase: 'replacing', currentPage: pages[0].name, progress: { done: 0, total: pages.length }, results: [], error: '' });

    const brandContext = await fetchBrandContext(scrapeUrl);

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      setStatus((prev) => ({ ...prev, currentPage: page.title || page.name, progress: { done: i, total: pages.length } }));
      try {
        const resp = await actionWebInvoke(
          REPLACE_PAGE_JCR_ACTION_URL,
          { Authorization: `Bearer ${ue.imsToken}` },
          {
            pagePath: page.path,
            siteSlug: ue.siteSlug,
            scrapedTexts: texts,
            damFolderPath: ue.damFolderPath || '',
            authorUrl: ue.authorUrl,
            orgId: ue.orgId,
            bearer: ue.imsToken,
            targetBrand: targetBrand || undefined,
            pageType: pageType || undefined,
            language: language || undefined,
            additionalContext: additionalContext || undefined,
            brandContext: brandContext || undefined,
          },
          { method: 'POST' }
        );
        const body = resp?.body || resp || {};
        results.push({
          page: page.name, title: page.title, path: page.path,
          ok: !body.error, applied: body.applied || 0, failed: body.failed || 0,
          snapshotSaved: body.snapshotSaved || false,
          changes: Array.isArray(body.changes) ? body.changes : [],
          error: body.error ? (typeof body.error === 'object' ? JSON.stringify(body.error) : body.error) : undefined,
        });
      } catch (err) {
        results.push({ page: page.name, title: page.title, ok: false, applied: 0, failed: 0, changes: [], error: err?.message || 'Action call failed' });
      }
      setStatus((prev) => ({ ...prev, progress: { done: i + 1, total: pages.length }, results: [...results] }));
    }

    const totalApplied = results.reduce((s, r) => s + r.applied, 0);
    const totalFailed = results.filter((r) => !r.ok).length;
    showToast('positive', `All pages done! ${totalApplied} replacements across ${pages.length} pages${totalFailed > 0 ? ` (${totalFailed} page errors)` : ''}`);
    setStatus({ phase: 'done', currentPage: '', progress: { done: pages.length, total: pages.length }, results, error: '' });

    // Record the whole site rebrand as ONE named step in the change history.
    const walEntries = results.filter((r) => r.ok).flatMap((r) => changesToWalEntries(r.changes));
    const walPages = results.filter((r) => r.ok && r.path).map((r) => r.path);
    if (walEntries.length > 0) {
      await appendWalStep((targetBrand || '').trim() || 'Rebrand', walPages, walEntries);
      refreshWalHistory();
    }

    setShowRefreshDialog(true);
  }, [discoveredPages, selectedPagePaths, ue.imsToken, ue.authorUrl, ue.orgId, ue.damFolderPath, ue.siteSlug, ue.pagesRoot, texts, targetBrand, pageType, language, additionalContext, scrapeUrl, fetchBrandContext, appendWalStep, refreshWalHistory]);

  // Time-travel to an absolute step (revert backward or redo forward).
  const handleWalRestore = useCallback(async (toSeq) => {
    if (!WAL_JCR_ACTION_URL) { showToast('negative', 'wal-jcr action not deployed'); return; }
    setRevertConfirm(null);
    setWalBusy(true);
    try {
      const resp = await actionWebInvoke(
        WAL_JCR_ACTION_URL,
        { Authorization: `Bearer ${ue.imsToken}` },
        { mode: 'restore', authorUrl: ue.authorUrl, siteSlug: ue.siteSlug, orgId: ue.orgId, bearer: ue.imsToken, toSeq },
        { method: 'POST' }
      );
      const body = resp?.body || resp || {};
      if (body.error) {
        showToast('negative', `Restore failed: ${body.error}`);
      } else {
        const label = body.target === 0 ? 'original content' : `step #${body.target} "${body.targetName || ''}"`;
        showToast('positive', `${body.direction === 'redo' ? 'Redone to' : 'Reverted to'} ${label} — ${body.applied} restored`);
        setShowRefreshDialog(true);
      }
    } catch (err) {
      showToast('negative', `Restore failed: ${err?.message || err}`);
    } finally {
      setWalBusy(false);
      refreshWalHistory();
    }
  }, [ue.imsToken, ue.authorUrl, ue.orgId, ue.siteSlug, refreshWalHistory]);

  // Load history once the site context is ready.
  useEffect(() => {
    if (ue.ready && ue.siteSlug && ue.imsToken && ue.authorUrl && !walHistory.loaded) {
      refreshWalHistory();
    }
  }, [ue.ready, ue.siteSlug, ue.imsToken, ue.authorUrl, walHistory.loaded, refreshWalHistory]);

  // --- DEV/TEST --- Comment the next block to remove dev tools before commit
  const { showDevTab, setShowDevTab, devTabProps } = useDevHandlers({
    ue, texts, collectPageComponents, collectTextEntry, getEditorContext, fetchDamEntities,
    actionUrls: { TEST_LLM_ACTION_URL, FETCH_NODE_JSON_ACTION_URL, UPLOAD_ACTION_URL, REPLACE_PAGE_JCR_ACTION_URL },
  });
  // --- /DEV/TEST ---

  const handleImport = useCallback(() => {
    if (!ue.conn) return;

    const getToken = () => {
      const sc = ue.conn?.sharedContext;
      const scAuth = sc?.get?.('auth');
      return scAuth?.imsToken || sc?.get?.('token') || '';
    };

    const isDark = ue.conn?.sharedContext?.get?.('theme') === 'dark';

    ImportAssetsDialogOpener.open({
      env: 'prod',
      projectId: ue.siteSlug || 'demo-pilot',
      accessToken: getToken,
      colorScheme: isDark ? 'dark' : 'light',
      onCompleted: (result) => {
        const images = (result.assets || [])
          .filter((a) => a && a.data && typeof a.data.src === 'string' && a.data.src)
          .filter((a) => !(a.data.mime && String(a.data.mime).includes('pallete')))
          .map((a) => a.data);

        const texts = (result.texts || []);

        if (images.length === 0 && texts.length === 0) {
          return;
        }

        setPendingAssets({ images, texts });
      },
      onError: (err) => {
        console.error('[DemoPilot] Import error:', err);
      },
      onCancel: () => {},
    });
  }, [ue.conn, ue.siteSlug]);

  return (
    <Provider theme={defaultTheme} colorScheme="light">
      <div className="dp-rail">
        <nav className="dp-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={activeTab === t.id}
              className={`dp-tab ${activeTab === t.id ? 'is-active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
          {/* --- DEV/TEST --- Comment the next block to remove dev tab before commit */}
          {showDevTab && (
            <button
              key="dev"
              role="tab"
              aria-selected={activeTab === 'dev'}
              className={`dp-tab dp-tab-dev ${activeTab === 'dev' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('dev')}
            >
              Dev
            </button>
          )}
          {/* --- /DEV/TEST --- */}
          <button
            type="button"
            className="dp-tab-action"
            onClick={handleImport}
            title="Import assets from a URL"
            aria-label="Import Assets"
          >
            <span className="dp-tab-action-icon" aria-hidden>+</span>
            <span className="dp-tab-action-label">Import</span>
          </button>
          {/* --- DEV/TEST --- Comment the next block to remove dev toggle before commit */}
          <button
            type="button"
            className={`dp-dev-toggle ${showDevTab ? 'is-active' : ''}`}
            onClick={() => {
              setShowDevTab((v) => !v);
              if (activeTab === 'dev') setActiveTab('generate');
            }}
            title={showDevTab ? 'Hide Dev tab' : 'Show Dev tab'}
            aria-label="Toggle developer tools"
          >
            &#9881;
          </button>
          {/* --- /DEV/TEST --- */}
        </nav>

        <main className="dp-body">
          {activeTab === 'images' && (
            <ImagesTab
              key={selectorRefreshKey}
              imsToken={ue.imsToken}
              imsOrg={ue.orgId}
              repositoryId={ue.repositoryId}
              damFolderPath={ue.damFolderPath}
              refreshKey={selectorRefreshKey}
              selection={ue.selection}
              uploadStatus={uploadStatus}
              onDismissUploadStatus={() => setUploadStatus({ inProgress: false, total: 0, done: 0, failed: 0 })}
              onAssetPick={handleAssetPick}
            />
          )}
          {activeTab === 'texts' && (
            <TextsTab
              texts={texts}
              onImportClick={handleImport}
              selection={ue.selection}
              selectedText={selectedText}
              canReplace={!!ue.imsToken && !!ue.authorUrl}
              replacingTextId={replacingTextId}
              onReplace={handleTextReplace}
              onReplaceAll={handleReplaceAllText}
              replaceAllStatus={replaceAllStatus}
            />
          )}
          {activeTab === 'generate' && (
            <AIGenerateTab
              texts={texts}
              damFolderPath={ue.damFolderPath}
              damAssetCount={damAssetCount}
              targetBrand={targetBrand}
              onTargetBrandChange={setTargetBrand}
              scrapeUrl={scrapeUrl}
              onScrapeUrlChange={setScrapeUrl}
              pageType={pageType}
              onPageTypeChange={setPageType}
              language={language}
              onLanguageChange={setLanguage}
              additionalContext={additionalContext}
              onAdditionalContextChange={setAdditionalContext}
              onImportClick={handleImport}
              onReplaceAllPage={handleReplaceAllPage}
              replacePageStatus={replacePageStatus}
              onDiscoverPages={handleDiscoverPages}
              onReplaceSelectedPages={handleReplaceSelectedPages}
              replaceAllPagesStatus={replaceAllPagesStatus}
              discoveredPages={discoveredPages}
              selectedPagePaths={selectedPagePaths}
              onSelectedPagePathsChange={setSelectedPagePaths}
              walHistory={walHistory}
              walBusy={walBusy}
              onRefreshHistory={refreshWalHistory}
              onRequestRestore={(seq, name, direction) => setRevertConfirm({ seq, name, direction })}
            />
          )}
          {/* --- DEV/TEST --- Comment the next line to remove dev tools before commit */}
          {activeTab === 'dev' && showDevTab && <DevTab {...devTabProps} />}
          {/* --- /DEV/TEST --- */}
        </main>

        {showRefreshDialog && (
          <div className="dp-refresh-overlay" role="alertdialog" aria-modal="true" aria-label="Page ready">
            <div className="dp-refresh-dialog">
              <button
                type="button"
                className="dp-icon-btn dp-refresh-close"
                aria-label="Close"
                onClick={() => setShowRefreshDialog(false)}
              >
                &times;
              </button>
              <div className="dp-refresh-icon" aria-hidden>&#10024;</div>
              <h2 className="dp-refresh-title">Generation complete!</h2>
              <p className="dp-refresh-body">
                All content has been replaced by AI.<br />
                Refresh the page to see the changes.
              </p>
              <kbd className="dp-refresh-kbd">&#8984; + R</kbd>
              <button
                type="button"
                className="dp-btn dp-btn-primary dp-refresh-dismiss"
                onClick={() => setShowRefreshDialog(false)}
              >
                Got it
              </button>
            </div>
          </div>
        )}

        {revertConfirm && (
          <div className="dp-refresh-overlay" role="alertdialog" aria-modal="true" aria-label="Confirm restore">
            <div className="dp-refresh-dialog">
              <button
                type="button"
                className="dp-icon-btn dp-refresh-close"
                aria-label="Close"
                onClick={() => setRevertConfirm(null)}
              >
                &times;
              </button>
              <div className="dp-refresh-icon" aria-hidden>{revertConfirm.direction === 'redo' ? '↪' : '↩'}</div>
              <h2 className="dp-refresh-title">
                {revertConfirm.direction === 'redo' ? 'Redo to this step?' : 'Revert to this step?'}
              </h2>
              <p className="dp-refresh-body">
                {revertConfirm.seq === 0
                  ? 'This restores the original content, before any AI rebrand.'
                  : <>This restores the site to the state at step <strong>“{revertConfirm.name}”</strong> (#{revertConfirm.seq}).</>}
                <br />You can move again afterwards with the history.
              </p>
              <div className="dp-confirm-actions">
                <button type="button" className="dp-btn dp-btn-ghost" onClick={() => setRevertConfirm(null)}>
                  Cancel
                </button>
                <button type="button" className="dp-btn dp-btn-primary" onClick={() => handleWalRestore(revertConfirm.seq)}>
                  {revertConfirm.direction === 'redo' ? 'Yes, redo' : 'Yes, revert'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Provider>
  );
}
